"use client";

import Image from "next/image";
import Link from "next/link";
import {
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useTheme } from "next-themes";

import { Footer, Nav } from "@/app/_components/sections";
import { IconCheck, IconCopy } from "@/app/_components/icons";
import { InstallSnippet } from "@/components/photon/install-snippet";
import { formatMatrixDate, slugify } from "./photon-data";
import {
  LATEST_AUDIO_BENCHMARK,
  LATEST_IMAGE_BENCHMARK,
  latencySpread,
  throughputAdvantagePct,
} from "./performance-data";
import { ModalityInputs } from "./modality";
import { PhotonPageMark } from "./photon-wordmark";
import type {
  BaselineRuntime,
  BenchmarkDataset,
  BenchRuntime,
} from "./release-types";

const CONTACT_URL = "mailto:contact@moondream.ai";
const BASELINES: BaselineRuntime[] = ["vllm", "sglang"];
const RUNTIME_LOGOS: Record<BenchRuntime, string> = {
  photon: "/logos/moondream.png",
  vllm: "/logos/vllm.png",
  sglang: "/logos/sglang.png",
};

const runtimeName = (bench: BenchmarkDataset, runtime: BenchRuntime) =>
  bench.environment.runtimes[runtime].split(" ")[0];

/** Baseline runtimes actually present in a dataset's throughput cells,
    canonical engines first (v4 datasets may add others: nemo, qwen-asr). */
const datasetBaselines = (bench: BenchmarkDataset): BenchRuntime[] => {
  const seen = [...new Set(bench.throughput.map((cell) => cell.runtime))];
  return [
    ...BASELINES.filter((runtime) => seen.includes(runtime)),
    ...seen.filter((runtime) => !BASELINES.includes(runtime)),
  ];
};

const downloadCsv = (
  filename: string,
  header: string[],
  rows: (string | number)[][],
) => {
  const escape = (value: string | number) =>
    `"${String(value).replaceAll('"', '""')}"`;
  const csv = [header, ...rows]
    .map((cols) => cols.map(escape).join(","))
    .join("\n");
  const url = URL.createObjectURL(
    new Blob([csv], { type: "text/csv;charset=utf-8" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const downloadThroughputCsv = (bench: BenchmarkDataset) =>
  downloadCsv(
    `photon-${bench.id}-${bench.environment.label.toLowerCase().replaceAll(" ", "-")}-throughput.csv`,
    [
      "model",
      "concurrency",
      "competitor",
      "multiple",
      "photon_rps_if_retained",
      "competitor_rps_if_retained",
      "advantage_pct",
      "photon_realtime_multiple_if_measured",
    ],
    bench.throughput.map((c) => [
      bench.models.find((m) => m.id === c.modelId)?.model ?? c.modelId,
      c.concurrency,
      bench.environment.runtimes[c.runtime],
      c.multiple,
      c.photonRps ?? "",
      c.baselineRps ?? "",
      +throughputAdvantagePct(c).toFixed(2),
      c.realtimeMultiple ?? "",
    ]),
  );

const ReproCommand = ({ command }: { command: string }) => {
  const onCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(command);
    }
    const el = e.currentTarget;
    el.dataset.copied = "1";
    setTimeout(() => {
      delete el.dataset.copied;
    }, 1200);
  };
  return (
    <div className="perf-repro">
      <span className="hero-install-prompt mono">$</span>
      <code className="perf-repro-cmd mono">{command}</code>
      <button
        type="button"
        className="hero-install-copy"
        aria-label="Copy reproduction command"
        onClick={onCopy}
      >
        <IconCopy size={13} />
      </button>
    </div>
  );
};

// The chart leads; its provenance (model revisions, runtime versions,
// hardware, sampling, date, repro command) is one click away in a collapsed
// methodology block rather than repeated in front of every reader.
const ChartPanel = ({
  eyebrow,
  title,
  sub,
  benchmark,
  runtimes,
  sampling,
  repro,
  onDownload,
  children,
}: {
  eyebrow: string;
  title: string;
  sub?: string;
  benchmark: BenchmarkDataset;
  runtimes?: BenchRuntime[];
  sampling: string;
  repro: string;
  onDownload: () => void;
  children: ReactNode;
}) => {
  const env = benchmark.environment;
  const runtimeIds =
    runtimes ?? (Object.keys(env.runtimes) as BenchRuntime[]);
  return (
    <div className="perf-chart">
      <div className="perf-chart-head">
        <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
          {eyebrow}
        </div>
        <h3 className="t-heading-md" style={{ marginTop: 4 }}>
          {title}
        </h3>
        {sub ? (
          <p
            className="t-body-sm"
            style={{ color: "var(--text-secondary)", marginTop: 6, maxWidth: 680 }}
          >
            {sub}
          </p>
        ) : null}
      </div>
      {children}
      <div className="perf-actions">
        <button type="button" className="matrix-csv" onClick={onDownload}>
          Raw data (CSV)
        </button>
      </div>
      <details className="perf-details">
        <summary className="mono">Methodology & repro</summary>
        <MethodologyBody
          benchmark={benchmark}
          runtimes={runtimeIds}
          sampling={sampling}
          repro={repro}
        />
      </details>
    </div>
  );
};

const MethodologyBody = ({
  benchmark,
  runtimes,
  sampling,
  repro,
}: {
  benchmark: BenchmarkDataset;
  runtimes?: BenchRuntime[];
  sampling: string;
  repro: string;
}) => {
  const env = benchmark.environment;
  const runtimeIds =
    runtimes ?? (Object.keys(env.runtimes) as BenchRuntime[]);
  return (
    <>
      <dl className="perf-meta">
        <div>
          <dt>Models</dt>
          <dd>
            {benchmark.models
              .map((m) => `${m.model} (${m.revision})`)
              .join(" · ")}
          </dd>
        </div>
        <div>
          <dt>Runtimes</dt>
          <dd>
            {runtimeIds.map((runtime) => env.runtimes[runtime]).join(" · ")}
          </dd>
        </div>
        <div>
          <dt>Hardware</dt>
          <dd>
            {[
              env.gpu,
              env.driver && `driver ${env.driver}`,
              env.cuda && `CUDA ${env.cuda}`,
            ]
              .filter(Boolean)
              .join(" · ")}
          </dd>
        </div>
        <div>
          <dt>Workload</dt>
          <dd>{env.workload}</dd>
        </div>
        {sampling ? (
          <div>
            <dt>Sampling</dt>
            <dd>{sampling}</dd>
          </div>
        ) : null}
        <div>
          <dt>Date</dt>
          <dd>{formatMatrixDate(env.date)}</dd>
        </div>
      </dl>
      {repro ? <ReproCommand command={repro} /> : null}
    </>
  );
};

const BarLegend = ({
  benchmark,
  runtimes,
  note,
}: {
  benchmark: BenchmarkDataset;
  runtimes: BenchRuntime[];
  note?: string;
}) => (
  <div className="perf-legend">
    {runtimes.map((runtime) => (
      <span className="perf-key" key={runtime}>
        <span className={`perf-swatch is-${runtime}`} />{" "}
        {benchmark.environment.runtimes[runtime]}
      </span>
    ))}
    {note ? <span className="perf-key-note mono">{note}</span> : null}
  </div>
);

// ---- 01 · Request throughput: engine bars per model × concurrency ----
// Every engine gets a bar, Photon included - length is measured speed
// (requests/second when the dataset retains absolutes, otherwise relative
// throughput with the baseline at 1×).
const ThroughputChart = ({
  benchmark: bench,
  concurrency,
}: {
  benchmark: BenchmarkDataset;
  concurrency: number;
}) => {
  const baselines = datasetBaselines(bench);
  // The dataset's nearest measured level to the page-wide selection
  // (audio measures c1/c8 only); each cluster labels its actual value.
  const displayConcurrency = [...bench.environment.concurrency].sort(
    (a, b) =>
      Math.abs(a - concurrency) - Math.abs(b - concurrency) || a - b,
  )[0];
  const formatMultiple = (multiple: number) =>
    `${multiple < 1.1 ? multiple.toFixed(2) : multiple.toFixed(1)}×`;
  const coverage = (runtime: BenchRuntime) =>
    new Set(
      bench.throughput
        .filter((cell) => cell.runtime === runtime)
        .map((cell) => cell.modelId),
    );
  // A model shows the runtimes measured against it - plus an explicit "not
  // available" row when a runtime covers every other model but this one
  // (e.g. SGLang cannot serve Moondream 3). Runtimes that are simply a
  // different model's reference engine (ASR) are not shown as gaps.
  const runtimesForModel = (modelId: string) =>
    baselines.filter((runtime) => {
      const covered = coverage(runtime);
      return covered.has(modelId) || covered.size === bench.models.length - 1;
    });
  const realtimeAt = (modelId: string) =>
    bench.throughput.find(
      (cell) => cell.modelId === modelId && cell.realtimeMultiple,
    );
  const hasAbsolute = bench.throughput.every(
    (cell) => cell.photonRps !== undefined,
  );

  type Bar = {
    runtime: BenchRuntime;
    /** Common-axis speed within the model card. */
    speed: number | null;
    label: string;
  };
  const modelBars = (modelId: string) => {
    const clusters = bench.environment.concurrency.map((concurrency) => {
      const cells = bench.throughput.filter(
        (cell) => cell.modelId === modelId && cell.concurrency === concurrency,
      );
      if (cells.length === 0) return null;
      const bars: Bar[] = [];
      if (hasAbsolute) {
        bars.push({
          runtime: "photon",
          speed: cells[0].photonRps!,
          label: cells[0].photonRps!.toFixed(2),
        });
      } else {
        // Relative axis: the (single) baseline sits at 1×.
        const best = Math.max(...cells.map((cell) => cell.multiple));
        bars.push({
          runtime: "photon",
          speed: best,
          label: formatMultiple(best),
        });
      }
      for (const runtime of runtimesForModel(modelId)) {
        const cell = cells.find((candidate) => candidate.runtime === runtime);
        if (!cell) {
          bars.push({ runtime, speed: null, label: "–" });
        } else if (hasAbsolute) {
          bars.push({
            runtime,
            speed: cell.baselineRps!,
            label: cell.baselineRps!.toFixed(2),
          });
        } else {
          bars.push({
            runtime,
            speed: bars[0].speed! / cell.multiple,
            label: "1×",
          });
        }
      }
      return { concurrency, bars };
    });
    return clusters.filter(Boolean) as {
      concurrency: number;
      bars: Bar[];
    }[];
  };

  return (
    <>
      <BarLegend
        benchmark={bench}
        runtimes={["photon", ...baselines]}
        note={
          hasAbsolute
            ? "Bar length = measured throughput, requests/second"
            : "Bar length = relative throughput, baseline engine = 1×"
        }
      />
      <div className="tp-heatmap-list">
        {bench.models.map((model) => {
          // Each carousel view fits its own scale: the axis maximum comes
          // from the displayed concurrency only, so bars fill the card
          // naturally at every level.
          const clusters = modelBars(model.id).filter(
            (cluster) => cluster.concurrency === displayConcurrency,
          );
          const axisMax = Math.max(
            ...clusters.flatMap((cluster) =>
              cluster.bars.map((bar) => bar.speed ?? 0),
            ),
          );
          return (
            <section className="tp-heatmap" key={model.id}>
              <div className="tp-heatmap-head">
                <span className="tp-heatmap-name">{model.model}</span>
                <span className="tp-heatmap-meta mono">{model.family}</span>
              </div>
              <div className="tpb-clusters">
                {/* Stable keys: the cluster and rows persist across
                    concurrency changes so bar widths transition instead
                    of remounting. */}
                {clusters.map(({ concurrency, bars }) => (
                  <div className="tpb-cluster" key="cluster">
                    <span className="tpb-conc mono">c{concurrency}</span>
                    <div className="tpb-rows">
                      {bars.map((bar) => (
                        <div className="tpb-row" key={bar.runtime}>
                          <span className="tpb-engine mono">
                            {runtimeName(bench, bar.runtime)}
                          </span>
                          <div
                            className="tpb-track"
                            role="img"
                            aria-label={
                              bar.speed === null
                                ? `${runtimeName(bench, bar.runtime)} is not available for ${model.model}`
                                : `${runtimeName(bench, bar.runtime)} throughput for ${model.model} at concurrency ${concurrency}: ${bar.label}${hasAbsolute ? " requests per second" : ""}`
                            }
                          >
                            {bar.speed !== null ? (
                              <i
                                className={`tpb-bar is-${bar.runtime}`}
                                style={{
                                  // 84% ceiling leaves room for the value label.
                                  width: `${Math.max(2, (bar.speed / axisMax) * 84)}%`,
                                }}
                              />
                            ) : null}
                            <span className="tpb-value mono tabular">
                              {bar.label}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {(() => {
                const realtime = realtimeAt(model.id);
                return realtime ? (
                  <p className="tp-heatmap-note mono">
                    {realtime.realtimeMultiple}× realtime at c
                    {realtime.concurrency}
                  </p>
                ) : null;
              })()}
            </section>
          );
        })}
      </div>
    </>
  );
};

// ---- Latency: engine bars per model × percentile - shorter is better ----
const LatencyChart = ({ benchmark: bench }: { benchmark: BenchmarkDataset }) => {
  // Typical and tail only - p95 sits between them and rarely changes the
  // story; all three percentiles remain in the CSV. One control rotates
  // every card between the two, mirroring the throughput carousel.
  const percentiles = [
    { key: "p50", label: "typical (p50)" },
    { key: "p99", label: "tail (p99)" },
  ] as const;
  const [selected, setSelected] = useState<(typeof percentiles)[number]>(
    percentiles[0],
  );
  const seen = [...new Set(bench.latency.map((row) => row.runtime))];
  const baselines = [
    ...BASELINES.filter((runtime) => seen.includes(runtime)),
    ...seen.filter((runtime) => !BASELINES.includes(runtime)),
  ];
  const formatLatency = (milliseconds: number) =>
    milliseconds < 1000
      ? `${Math.round(milliseconds)}ms`
      : `${(milliseconds / 1000).toFixed(milliseconds >= 10000 ? 1 : 2)}s`;
  const modelIds = [...new Set(bench.latency.map((row) => row.modelId))];

  return (
    <>
      <div className="perf-chart-controls">
        <p className="perf-hint mono">Shorter is better →</p>
        <div className="seg" role="group" aria-label="Percentile">
          {percentiles.map((percentile) => (
            <button
              key={percentile.key}
              type="button"
              className={selected.key === percentile.key ? "is-active" : ""}
              onClick={() => setSelected(percentile)}
            >
              {percentile.label}
            </button>
          ))}
        </div>
      </div>
      <BarLegend
        benchmark={bench}
        runtimes={["photon", ...baselines]}
        note="Bar length = client-observed completion latency"
      />
      <div className="tp-heatmap-list">
        {modelIds.map((modelId) => {
          const comparisons = bench.latency
            .filter((row) => row.modelId === modelId)
            .sort(
              (a, b) =>
                baselines.indexOf(a.runtime) - baselines.indexOf(b.runtime),
            );
          const model = bench.models.find(
            (candidate) => candidate.id === modelId,
          );
          // Photon ran once; every comparison carries the same Photon
          // percentiles, so one Photon bar leads each cluster.
          const rowsFor = (percentile: (typeof percentiles)[number]["key"]) => [
            {
              runtime: "photon" as BenchRuntime,
              value: comparisons[0].photon[percentile],
            },
            ...comparisons.map((comparison) => ({
              runtime: comparison.runtime,
              value: comparison.baseline[percentile],
            })),
          ];
          // Fit the displayed percentile's own scale, like throughput.
          const axisMax = Math.max(
            ...rowsFor(selected.key).map((row) => row.value),
          );
          return (
            <section className="tp-heatmap" key={modelId}>
              <div className="tp-heatmap-head">
                <span className="tp-heatmap-name">
                  {model?.model ?? modelId}
                </span>
                <span className="tp-heatmap-meta mono">
                  c{comparisons[0].concurrency}
                </span>
              </div>
              <div className="tpb-clusters">
                {/* Stable keys so bar widths transition between percentiles
                    instead of remounting. */}
                {[selected].map((percentile) => (
                  <div className="tpb-cluster is-wide" key="cluster">
                    <span className="tpb-conc mono">{percentile.label}</span>
                    <div className="tpb-rows">
                      {rowsFor(percentile.key).map(({ runtime, value }) => (
                        <div className="tpb-row" key={runtime}>
                          <span className="tpb-engine mono">
                            {runtimeName(bench, runtime)}
                          </span>
                          <div
                            className="tpb-track"
                            role="img"
                            aria-label={`${runtimeName(bench, runtime)} ${percentile.key} completion latency for ${model?.model ?? modelId}: ${value.toFixed(0)} milliseconds`}
                          >
                            <i
                              className={`tpb-bar is-${runtime}`}
                              style={{
                                width: `${Math.max(2, (value / axisMax) * 84)}%`,
                              }}
                            />
                            <span className="tpb-value mono tabular">
                              {formatLatency(value)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
};

// ---- 03 · Cold start: runtime markers on a shared time axis ----
const ColdStartChart = ({ benchmark: bench }: { benchmark: BenchmarkDataset }) => {
  const modelIds = [...new Set(bench.coldStart.map((row) => row.modelId))];
  const baselines = BASELINES.filter((runtime) =>
    bench.coldStart.some((comparison) => comparison.runtime === runtime),
  );
  const runtimes: BenchRuntime[] = ["photon", ...baselines];
  const allTotals = bench.coldStart.flatMap((comparison) => [
    comparison.photonSeconds,
    comparison.baselineSeconds,
  ]);
  const axisMax = Math.ceil(Math.max(...allTotals) / 50) * 50;
  const ticks = Array.from({ length: 5 }, (_, index) =>
    Math.round((axisMax * index) / 4),
  );
  const formatSeconds = (seconds: number) =>
    `${seconds < 100 ? seconds.toFixed(1) : Math.round(seconds)}s`;
  return (
    <>
      <div className="cold-timeline-legend">
        {runtimes.map((runtime) => (
          <span className="cold-timeline-key" key={runtime}>
            <Image
              src={RUNTIME_LOGOS[runtime]}
              alt=""
              width={18}
              height={18}
            />
            {runtimeName(bench, runtime)}
          </span>
        ))}
        <span className="perf-key-note mono">
          Shared scale across models · lower is better
        </span>
      </div>
      <div className="cold-timeline">
        <div className="cold-timeline-header">
          <span className="cold-timeline-corner mono">Model</span>
          <div className="cold-timeline-axis" aria-hidden="true">
            {ticks.map((tick) => (
              <span
                className="mono tabular"
                key={tick}
                style={{ left: `${(tick / axisMax) * 100}%` }}
              >
                {tick}s
              </span>
            ))}
          </div>
        </div>
        {modelIds.map((modelId) => {
          const model = bench.models.find(
            (candidate) => candidate.id === modelId,
          );
          const comparisons = bench.coldStart.filter(
            (comparison) => comparison.modelId === modelId,
          );
          const totalForRuntime = (runtime: BenchRuntime) =>
            runtime === "photon"
              ? comparisons[0].photonSeconds
              : comparisons.find(
                  (comparison) => comparison.runtime === runtime,
                )?.baselineSeconds;
          return (
            <div className="cold-timeline-row" key={modelId}>
              <span className="cold-timeline-model">
                <strong>{model?.model ?? modelId}</strong>
              </span>
              <div className="cold-timeline-plot">
                <span className="cold-timeline-track" aria-hidden="true" />
                {ticks.map((tick) => (
                  <i
                    className="cold-timeline-gridline"
                    key={tick}
                    style={{ left: `${(tick / axisMax) * 100}%` }}
                    aria-hidden="true"
                  />
                ))}
                {runtimes.map((runtime) => {
                  const total = totalForRuntime(runtime);
                  if (total === undefined) return null;
                  const position = (total / axisMax) * 100;
                  const label = runtimeName(bench, runtime);
                  return (
                    <span
                      className={`cold-timeline-marker is-${runtime}${
                        position > 84 ? " is-edge" : ""
                      }`}
                      key={runtime}
                      style={
                        {
                          "--cold-position": `${position}%`,
                        } as CSSProperties
                      }
                      aria-label={`${label} time to first response for ${model?.model ?? modelId}: ${total.toFixed(2)} seconds`}
                      role="img"
                      title={`${label}: ${total.toFixed(2)} seconds`}
                    >
                      <i className="cold-timeline-stem" aria-hidden="true" />
                      <span className="cold-timeline-logo">
                        <Image
                          src={RUNTIME_LOGOS[runtime]}
                          alt=""
                          width={20}
                          height={20}
                        />
                      </span>
                      <span className="cold-timeline-value mono tabular">
                        {formatSeconds(total)}
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

// ---- 04 · Resource use: VRAM, power, temperature, energy per token ----
const ResourceChart = ({ benchmark: bench }: { benchmark: BenchmarkDataset }) => {
  return (
    <div className="matrix-panel perf-resource-panel">
      <table className="matrix-table">
        <thead>
          <tr>
            <th>Model</th>
            <th>VRAM</th>
            <th>Power</th>
            <th>Temp</th>
            <th>J / output token</th>
          </tr>
        </thead>
        <tbody>
          {bench.models.map((model) => {
            const row = bench.resources.find(
              (resource) =>
                resource.modelId === model.id && resource.runtime === "photon",
            );
            if (!row) throw new Error(`Missing Photon resource row: ${model.id}`);
            return (
              <tr key={model.id}>
                <td>
                  <span className="matrix-model-name">{model.model}</span>
                </td>
                <td className="mono tabular matrix-dim">
                  {row.vramGb.toFixed(1)} GB
                </td>
                <td className="mono tabular matrix-dim">{row.powerW} W</td>
                <td className="mono tabular matrix-dim">{row.tempC} °C</td>
                <td className="mono tabular">{row.joulesPerToken.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// The headline claim, derived from a dataset so it can never overclaim.
const ledeFor = (bench: BenchmarkDataset) => {
  const cellsAhead = bench.throughput.filter((c) => c.multiple > 1).length;
  const maxMultiple = Math.max(...bench.throughput.map((c) => c.multiple));
  const names = datasetBaselines(bench).map((r) => runtimeName(bench, r));
  const baselineNames =
    names.length > 1
      ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
      : names[0];
  const scope =
    cellsAhead === bench.throughput.length
      ? `all ${bench.throughput.length}`
      : `${cellsAhead} of ${bench.throughput.length}`;
  // Whole numbers only: nobody cares about the hundredths.
  return `Faster than ${baselineNames} in ${scope} matched tests on ${bench.environment.label} , up to ${Math.round(maxMultiple)}×.`;
};

// One dataset's setup facts, labeled by modality - stacked inside the
// page's single collapsed "Test setup & disclosures" block.
const SetupBlock = ({
  modality,
  bench,
}: {
  modality: string;
  bench: BenchmarkDataset;
}) => {
  const env = bench.environment;
  return (
    <div className="perf-setup-block">
      <p className="perf-group-env mono">
        <ModalityInputs modality={modality} size={13} /> · {env.label} ·{" "}
        {env.workload} · {formatMatrixDate(env.date)}
      </p>
      <div>
        {env.qualityCaveat ? (
          <p
            className="t-body-sm"
            style={{ color: "var(--text-secondary)", margin: "12px 0 0" }}
          >
            {env.qualityCaveat}
          </p>
        ) : null}
        <dl className="env-grid">
          <div>
            <dt>GPU</dt>
            <dd>
              <Link
                href={`/photon/hardware/${slugify(env.label.split(" ")[0])}`}
                className="matrix-hw-link"
              >
                {env.gpu}
              </Link>
            </dd>
          </div>
          <div>
            <dt>Engines</dt>
            <dd>{Object.values(env.runtimes).join(" · ")}</dd>
          </div>
          <div>
            <dt>Workload</dt>
            <dd>{env.workload}</dd>
          </div>
          <div>
            <dt>Concurrency</dt>
            <dd>{env.concurrency.join(", ")}</dd>
          </div>
          <div>
            <dt>Updated</dt>
            <dd>{formatMatrixDate(env.date)}</dd>
          </div>
        </dl>
        {env.disclosures.length > 0 ? (
          <ul className="env-disclosures">
            {env.disclosures.map((disclosure) => (
              <li key={disclosure}>
                <IconCheck size={14} /> {disclosure}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

// The lead chart: image+text throughput first, audio throughput directly
// under it - one component, one reading rule, per-modality legends and
// methodology.
const CombinedThroughputPanel = ({
  image,
  audio,
}: {
  image?: BenchmarkDataset;
  audio?: BenchmarkDataset;
}) => {
  const concurrencyLevels = [
    ...new Set(
      [image, audio]
        .filter((bench): bench is BenchmarkDataset => Boolean(bench))
        .flatMap((bench) => bench.environment.concurrency),
    ),
  ].sort((a, b) => a - b);
  const [concurrency, setConcurrency] = useState(concurrencyLevels[0] ?? 1);
  const datasets = [
    image ? { id: "image-text", modality: "Image + text", bench: image } : null,
    audio ? { id: "audio", modality: "Audio", bench: audio } : null,
  ].filter(
    (
      entry,
    ): entry is { id: string; modality: string; bench: BenchmarkDataset } =>
      entry !== null,
  );
  const pairCount = datasets.reduce(
    (total, { bench }) => total + bench.throughput.length,
    0,
  );
  const modelCount = datasets.reduce(
    (total, { bench }) => total + bench.models.length,
    0,
  );
  return (
    <div className="perf-chart">
      <div className="perf-chart-head">
        <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
          01 · Request throughput
        </div>
        <h3 className="t-heading-md" style={{ marginTop: 4 }}>
          Throughput by concurrency
        </h3>
        <p
          className="t-body-sm"
          style={{ color: "var(--text-secondary)", marginTop: 6, maxWidth: 680 }}
        >
          How fast each engine serves the same workload. {pairCount} matched
          pairs · {modelCount} models.
        </p>
      </div>
      <div className="perf-chart-controls">
        <p className="perf-hint mono">Longer is better →</p>
        <div className="filter-field">
          <span className="filter-label mono">Batch size</span>
          <div className="seg" role="group" aria-label="Batch size">
            {concurrencyLevels.map((level) => (
              <button
                key={level}
                type="button"
                className={concurrency === level ? "is-active" : ""}
                onClick={() => setConcurrency(level)}
              >
                c{level}
              </button>
            ))}
          </div>
        </div>
      </div>
      {datasets.map(({ id, modality, bench }) => (
        <div className="perf-subgroup" id={id} key={id}>
          <div className="perf-subgroup-head">
            <span className="t-heading-sm perf-group-title">
              <ModalityInputs modality={modality} size={14} />
            </span>
            <span className="perf-group-env mono">
              {bench.environment.label} · {bench.environment.workload} ·{" "}
              {formatMatrixDate(bench.environment.date)}
            </span>
          </div>
          {id === "audio" ? (
            <p
              className="t-body-sm"
              style={{
                color: "var(--text-secondary)",
                margin: "0 0 4px",
                maxWidth: 680,
              }}
            >
              {ledeFor(bench)}
            </p>
          ) : null}
          <ThroughputChart benchmark={bench} concurrency={concurrency} />
        </div>
      ))}
      <div className="perf-actions">
        {datasets.map(({ id, modality, bench }) => (
          <button
            key={id}
            type="button"
            className="matrix-csv"
            onClick={() => downloadThroughputCsv(bench)}
          >
            Raw data · {modality} (CSV)
          </button>
        ))}
      </div>
      <details className="perf-details">
        <summary className="mono">Methodology & repro</summary>
        {datasets.map(({ id, modality, bench }) => (
          <div className="perf-setup-block" key={id}>
            <p className="perf-group-env mono">
              <ModalityInputs modality={modality} size={13} />
            </p>
            <MethodologyBody
              benchmark={bench}
              sampling={bench.environment.sampling.throughput}
              repro={
                bench.environment.repro
                  ? `${bench.environment.repro} --chart throughput`
                  : ""
              }
            />
          </div>
        ))}
      </details>
    </div>
  );
};

// A dataset's charts, numbered continuously across the page. `only` lets
// the page interleave datasets (both throughput charts first, depth after);
// `tag` keeps a detached chart attributable to its modality.
const ChartStack = ({
  benchmark: bench,
  firstIndex,
  tag,
}: {
  benchmark: BenchmarkDataset;
  firstIndex: number;
  tag?: string;
}) => {
  const env = bench.environment;
  const photonResourceRepro = env.repro.replace(
    /--engines\s+\S+/,
    "--engines photon",
  );
  let chartIndex = firstIndex;
  const eyebrow = (label: string) =>
    `${String(chartIndex++).padStart(2, "0")} · ${label}${tag ? ` · ${tag}` : ""}`;

  return (
    <>
      {bench.latency.length > 0 ? (
        <ChartPanel
          eyebrow={eyebrow("Latency & jitter")}
          title="Request completion latency"
          sub="Typical (p50) and tail (p99) completion latency at concurrency 1. All percentiles are in the CSV."
          benchmark={bench}
          sampling={env.sampling.latency}
          repro={`${env.repro} --chart latency`}
          onDownload={() =>
            downloadCsv(
              `photon-${bench.id}-${env.label.toLowerCase()}-latency.csv`,
              [
                "model",
                "concurrency",
                "runtime",
                "p50_ms",
                "p95_ms",
                "p99_ms",
                "spread_ms",
              ],
              bench.latency.flatMap((comparison) =>
                ([
                  ["photon", comparison.photon],
                  [comparison.runtime, comparison.baseline],
                ] as const).map(([runtime, values]) => [
                  bench.models.find((m) => m.id === comparison.modelId)
                    ?.model ?? comparison.modelId,
                  comparison.concurrency,
                  bench.environment.runtimes[runtime],
                  values.p50,
                  values.p95,
                  values.p99,
                  latencySpread(values),
                ]),
              ),
            )
          }
        >
          <LatencyChart benchmark={bench} />
        </ChartPanel>
      ) : null}

      {bench.coldStart.length > 0 ? (
        <ChartPanel
          eyebrow={eyebrow("Cold start")}
          title="Time to first response"
          sub="Process start to first response, lower is better."
          benchmark={bench}
          sampling={env.sampling.coldStart}
          repro={`${env.repro} --chart cold-start`}
          onDownload={() =>
            downloadCsv(
              `photon-${bench.id}-${env.label.toLowerCase()}-cold-start.csv`,
              [
                "model",
                "competitor",
                "photon_total_s",
                "competitor_api_ready_s_if_recorded",
                "competitor_total_s",
                "photon_reduction_pct",
              ],
              bench.coldStart.map((comparison) => [
                bench.models.find((m) => m.id === comparison.modelId)
                  ?.model ?? comparison.modelId,
                bench.environment.runtimes[comparison.runtime],
                comparison.photonSeconds,
                comparison.baselineReadySeconds ?? "",
                comparison.baselineSeconds,
                +(
                  (1 -
                    comparison.photonSeconds /
                      comparison.baselineSeconds) *
                  100
                ).toFixed(2),
              ]),
            )
          }
        >
          <ColdStartChart benchmark={bench} />
        </ChartPanel>
      ) : null}

      {bench.resources.length > 0 ? (
        <ChartPanel
          eyebrow={eyebrow("Resource use")}
          title="Memory, power, and energy per token"
          sub="Photon's GPU memory, power, temperature, and energy per output token."
          benchmark={bench}
          runtimes={["photon"]}
          sampling={env.sampling.resources}
          repro={`${photonResourceRepro} --chart resources`}
          onDownload={() =>
            downloadCsv(
              `photon-${bench.id}-${env.label.toLowerCase()}-resources.csv`,
              [
                "model",
                "vram_gb",
                "power_w",
                "temp_c",
                "joules_per_output_token",
              ],
              bench.resources
                .filter((resource) => resource.runtime === "photon")
                .map((resource) => [
                  bench.models.find((m) => m.id === resource.modelId)?.model ??
                    resource.modelId,
                  resource.vramGb,
                  resource.powerW,
                  resource.tempC,
                  resource.joulesPerToken,
                ]),
            )
          }
        >
          <ResourceChart benchmark={bench} />
        </ChartPanel>
      ) : null}
    </>
  );
};

export function PerformancePage() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const theme: "dark" | "light" =
    mounted && resolvedTheme === "dark" ? "dark" : "light";

  const lede = LATEST_IMAGE_BENCHMARK ? ledeFor(LATEST_IMAGE_BENCHMARK) : "";

  return (
    <div className="md-landing">
      <Nav
        theme={theme}
        onToggleTheme={() => setTheme(theme === "dark" ? "light" : "dark")}
      />

      <main>
        {/* The lede: one derived claim, the dataset picker, and the full
            setup one click away. The data starts immediately below. */}
        <section
          className="section"
          style={{ paddingTop: 72, paddingBottom: 40 }}
        >
          <div className="container">
            <PhotonPageMark section="Performance" />
            <h1
              className="t-display-md"
              style={{ marginTop: 12, maxWidth: 780 }}
            >
              Performance.
            </h1>
            <p
              className="t-body-lg"
              style={{
                color: "var(--text-secondary)",
                marginTop: 16,
                maxWidth: 680,
              }}
            >
              {lede}
            </p>
            <details className="perf-details perf-setup">
              <summary className="mono">Test setup & disclosures</summary>
              {LATEST_IMAGE_BENCHMARK ? (
                <SetupBlock
                  modality="Image + text"
                  bench={LATEST_IMAGE_BENCHMARK}
                />
              ) : null}
              {LATEST_AUDIO_BENCHMARK ? (
                <SetupBlock modality="Audio" bench={LATEST_AUDIO_BENCHMARK} />
              ) : null}
              <p
                className="t-caption mono"
                style={{ color: "var(--text-tertiary)", marginTop: 12 }}
              >
                <Link href="/blog/photon-2-launch" className="link">
                  Launch post
                </Link>{" "}
                · full context and analysis.
              </p>
            </details>
          </div>
        </section>

        {/* Charts: one throughput component holding both modalities -
            image+text results first, audio directly under them - then the
            image+text depth charts. No dataset picker. */}
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container perf-stack">
            <CombinedThroughputPanel
              image={LATEST_IMAGE_BENCHMARK}
              audio={LATEST_AUDIO_BENCHMARK}
            />
            {LATEST_IMAGE_BENCHMARK ? (
              <ChartStack
                benchmark={LATEST_IMAGE_BENCHMARK}
                firstIndex={2}
                tag="Image + text"
              />
            ) : null}
          </div>
        </section>

        <section className="section final-cta" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="final-card perf-final-card card">
              <div className="final-inner">
                <div>
                  <div
                    className="t-eyebrow"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    Your workload
                  </div>
                  <h2 className="t-heading-xl" style={{ marginTop: 10 }}>
                    Test Photon on your stack.
                  </h2>
                  <p
                    className="t-body-lg"
                    style={{
                      color: "var(--text-secondary)",
                      marginTop: 12,
                      maxWidth: 620,
                    }}
                  >
                    Use the published command as a starting point, or talk to
                    us about your model, hardware, and latency target.
                  </p>
                  <div style={{ marginTop: 24 }}>
                    <InstallSnippet />
                  </div>
                </div>
                <div className="final-ctas">
                  <a href={CONTACT_URL} className="btn btn-secondary">
                    Talk to us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
